import { useId } from 'react'
import { useIntl } from 'react-intl'

import {
  SUPPORT_TEXT_MAX_LENGTH,
  type SupportBlocking,
  SupportBlockingZod,
  type SupportCategory,
  SupportCategoryZod,
} from '@onr/schema'
import { ChevronLeftIcon } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { CharacterCounter } from '@/components/atoms/CharacterCounter'
import { ChoiceButton } from '@/components/atoms/ChoiceButton'
import { InlineError } from '@/components/atoms/InlineError'
import { TextArea } from '@/components/atoms/TextArea/TextArea.tsx'
import { SupportContextBox, type SupportContextBoxProps } from '@/components/molecules/SupportContextBox'

export const SUPPORT_COUNTER_THRESHOLD = 3500

const CATEGORIES_WITH_EXAMPLE = ['technical', 'usage', 'access', 'data'] as const satisfies SupportCategory[]

const hasExample = (category: SupportCategory): category is (typeof CATEGORIES_WITH_EXAMPLE)[number] =>
  (CATEGORIES_WITH_EXAMPLE as ReadonlyArray<SupportCategory>).includes(category)

export interface SupportRequestFormValues {
  readonly category: SupportCategory | null
  readonly blocking: SupportBlocking | null
  readonly text: string
}

export interface SupportRequestFormProps {
  readonly values: SupportRequestFormValues
  readonly context: Omit<SupportContextBoxProps, 'className'>
  readonly submitting: boolean
  readonly error: boolean
  readonly onChange: (values: Partial<SupportRequestFormValues>) => void
  readonly onBack: () => void
  readonly onCancel: () => void
  readonly onSubmit: () => void
}

export const isSupportRequestComplete = ({ category, blocking, text }: SupportRequestFormValues) =>
  category !== null && blocking !== null && text.trim() !== ''

export const SupportRequestForm = ({
  values,
  context,
  submitting,
  error,
  onChange,
  onBack,
  onCancel,
  onSubmit,
}: SupportRequestFormProps) => {
  const intl = useIntl()
  const titleId = useId()
  const fieldId = useId()
  const selectedCategory = values.category
  const descriptionPlaceholder =
    selectedCategory === null
      ? intl.formatMessage({ id: 'soutien.form.description.placeholder' })
      : hasExample(selectedCategory)
        ? intl.formatMessage({ id: `soutien.form.description.${selectedCategory}.placeholder` })
        : undefined

  return (
    <section aria-labelledby={titleId} className="flex h-full flex-col">
      <header className="flex flex-col gap-2.5 border-b border-gray-200 px-6 py-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex cursor-pointer items-center gap-1 self-start text-sm text-blue-700 hover:text-blue-600">
          <ChevronLeftIcon aria-hidden className="size-3.5" />
          {intl.formatMessage({ id: 'soutien.form.back' })}
        </button>
        <h2 id={titleId} className="font-heading text-lg font-semibold text-gray-900">
          {intl.formatMessage({ id: 'soutien.form.title' })}
        </h2>
      </header>

      <form
        id={`${fieldId}-form`}
        className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pt-5 pb-6"
        onSubmit={(event) => {
          event.preventDefault()
          if (isSupportRequestComplete(values) && !submitting) {
            onSubmit()
          }
        }}>
        <fieldset className="flex flex-col">
          <legend className="mb-2.5 font-bold text-gray-800">
            {intl.formatMessage({ id: 'soutien.form.category.legend' })}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {SupportCategoryZod.options.map((category) => (
              <ChoiceButton
                key={category}
                shape="tile"
                pressed={values.category === category}
                onClick={() => onChange({ category })}>
                <span className="font-bold text-gray-900">
                  {intl.formatMessage({ id: `soutien.category.${category}` })}
                </span>
                <span className="text-sm leading-snug text-gray-600">
                  {intl.formatMessage({ id: `soutien.category.${category}.hint` })}
                </span>
              </ChoiceButton>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col">
          <legend className="mb-2.5 font-bold text-gray-800">
            {intl.formatMessage({ id: 'soutien.form.blocking.legend' })}
          </legend>
          <div className="flex flex-wrap gap-2">
            {SupportBlockingZod.options.map((blocking) => (
              <ChoiceButton
                key={blocking}
                pressed={values.blocking === blocking}
                onClick={() => onChange({ blocking })}>
                {intl.formatMessage({ id: `soutien.blocking.${blocking}` })}
              </ChoiceButton>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-col gap-2">
          <label htmlFor={`${fieldId}-text`} className="font-bold text-gray-800">
            {intl.formatMessage({
              id:
                selectedCategory === null
                  ? 'soutien.form.description'
                  : `soutien.form.description.${selectedCategory}.label`,
            })}
          </label>
          {selectedCategory !== null && (
            <p id={`${fieldId}-help`} className="text-sm leading-snug text-gray-600">
              {intl.formatMessage({ id: `soutien.form.description.${selectedCategory}.help` })}
            </p>
          )}
          <TextArea
            id={`${fieldId}-text`}
            rows={8}
            value={values.text}
            maxLength={SUPPORT_TEXT_MAX_LENGTH}
            aria-describedby={selectedCategory === null ? undefined : `${fieldId}-help`}
            placeholder={descriptionPlaceholder}
            className="resize-y border-gray-300 py-2 text-base leading-[1.45] text-gray-900 placeholder:text-gray-400"
            onChange={(event) => onChange({ text: event.target.value })}
          />
          <CharacterCounter
            length={values.text.length}
            max={SUPPORT_TEXT_MAX_LENGTH}
            showFrom={SUPPORT_COUNTER_THRESHOLD}
          />
        </div>

        <SupportContextBox {...context} />
      </form>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-4">
        {error ? (
          <InlineError className="flex-[1_1_220px]" message={intl.formatMessage({ id: 'soutien.form.error' })} />
        ) : (
          <p className="flex-[1_1_220px] text-sm leading-snug text-gray-600">
            {intl.formatMessage({ id: 'soutien.form.help' })}
          </p>
        )}
        <div className="flex gap-2">
          <Button className="h-10 px-5" onClick={onCancel}>
            {intl.formatMessage({ id: 'soutien.form.cancel' })}
          </Button>
          <Button
            type="submit"
            form={`${fieldId}-form`}
            variant="primary"
            className="h-10 px-5"
            loading={submitting}
            disabled={!isSupportRequestComplete(values)}>
            {intl.formatMessage({ id: 'soutien.form.submit' })}
          </Button>
        </div>
      </footer>
    </section>
  )
}
